import React from "react";
import {
  Box,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { EmojiEvents, AutoAwesome, SwapHoriz } from "@mui/icons-material";

/**
 * Ranked vs For fun for karaoke songs and dance tracks.
 *
 * Three ways a track can be set up:
 *   - Player's choice (default): the card shows no ladder; the player picks
 *     Ranked or For fun on the track sheet. `ladder` is the preselected one.
 *   - Ranked: hard-set. Every entry and live battle is ranked; the card says
 *     so and there is no switch.
 *   - For fun: hard-set the same way.
 * A live-only song is always Ranked. Challenges have no ladder.
 *
 * Stored as two fields on the track: `ladder` ('ranked' | 'for_fun') and
 * `ladderLocked` (true = hard-set).
 */
export const LADDER_OPTIONS = [
  {
    value: "ranked",
    label: "Ranked",
    hint: "Votes can change rank.",
    color: "warning",
    Icon: EmojiEvents,
  },
  {
    value: "for_fun",
    label: "For fun",
    hint: "Rank stays the same.",
    color: "info",
    Icon: AutoAwesome,
  },
];

export const LADDER_MODE = {
  CHOICE: "choice",
  RANKED: "ranked",
  FOR_FUN: "for_fun",
};

/** Missing (documents saved before the field existed) reads as ranked. */
export const normalizeLadder = (ladder) =>
  ladder === "for_fun" ? "for_fun" : "ranked";

/** Missing `ladderLocked` (older documents) means the player chooses. */
export const ladderModeFromTrack = (track) => {
  if (track?.ladderLocked === true) return normalizeLadder(track?.ladder);
  return LADDER_MODE.CHOICE;
};

/**
 * Fields to send for a mode. In Player's choice, `ladder` is the option
 * preselected on the track sheet (`preselected`, defaults to ranked).
 */
export const ladderFieldsFromMode = (mode, preselected = "ranked") => {
  if (mode === LADDER_MODE.RANKED) return { ladder: "ranked", ladderLocked: true };
  if (mode === LADDER_MODE.FOR_FUN) return { ladder: "for_fun", ladderLocked: true };
  return { ladder: normalizeLadder(preselected), ladderLocked: false };
};

/** Compact chip for list tables. Shows the mode, not just the default. */
export const LadderChip = ({ ladder, ladderLocked, liveOnly = false, size = "small", sx }) => {
  const locked = liveOnly || ladderLocked === true;
  if (!locked) {
    return (
      <Chip
        icon={<SwapHoriz sx={{ fontSize: 16 }} />}
        label="Player's choice"
        size={size}
        variant="outlined"
        sx={{ fontWeight: 600, fontSize: "12px", ...sx }}
      />
    );
  }
  const option =
    LADDER_OPTIONS.find((o) => o.value === normalizeLadder(liveOnly ? "ranked" : ladder)) ||
    LADDER_OPTIONS[0];
  const Icon = option.Icon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={option.label}
      color={option.color}
      size={size}
      variant="filled"
      sx={{ fontWeight: 600, fontSize: "12px", ...sx }}
    />
  );
};

/**
 * Mode radio group used on the karaoke / dance upload & edit forms.
 * @param {{
 *   mode: 'choice'|'ranked'|'for_fun',
 *   onModeChange: (mode: string) => void,
 *   preselected?: 'ranked'|'for_fun',
 *   onPreselectedChange?: (ladder: string) => void,
 *   liveOnly?: boolean,
 *   isEdit?: boolean
 * }} props
 */
const LadderField = ({
  mode,
  onModeChange,
  preselected = "ranked",
  onPreselectedChange,
  liveOnly = false,
  isEdit = false,
}) => {
  const current = liveOnly ? LADDER_MODE.RANKED : mode || LADDER_MODE.CHOICE;
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>
        Mode
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {liveOnly
          ? "Live only songs are Ranked. Players cannot switch to For fun."
          : "Player's choice shows no mode on the card; the player picks when they open the track. Ranked or For fun hard-sets it: the card shows it and there is no switch."}
        {isEdit && !liveOnly
          ? " Changing this only affects new entries; existing posts keep the ladder they were created on."
          : ""}
      </Typography>
      <RadioGroup
        row
        value={current}
        onChange={(e) => !liveOnly && onModeChange(e.target.value)}
        aria-label="Mode"
      >
        <FormControlLabel
          value={LADDER_MODE.CHOICE}
          control={<Radio />}
          disabled={liveOnly}
          sx={{ mr: 3, alignItems: "flex-start", mt: 0.5 }}
          label={
            <Box sx={{ pt: "9px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <SwapHoriz sx={{ fontSize: 16 }} />
                <span style={{ fontSize: "14px", fontWeight: 600 }}>Player's choice</span>
              </Box>
              <span style={{ fontSize: "11px", color: "gray", display: "block" }}>
                Nothing on the card. They pick after tapping it.
              </span>
            </Box>
          }
        />
        {LADDER_OPTIONS.map(({ value: optionValue, label, hint, Icon }) => (
          <FormControlLabel
            key={optionValue}
            value={optionValue}
            control={<Radio />}
            disabled={liveOnly && optionValue !== "ranked"}
            sx={{ mr: 3, alignItems: "flex-start", mt: 0.5 }}
            label={
              <Box sx={{ pt: "9px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Icon sx={{ fontSize: 16 }} />
                  <span style={{ fontSize: "14px", fontWeight: 600 }}>{label} only</span>
                </Box>
                <span style={{ fontSize: "11px", color: "gray", display: "block" }}>
                  Shown on the card. {hint}
                </span>
              </Box>
            }
          />
        ))}
      </RadioGroup>
      {current === LADDER_MODE.CHOICE && onPreselectedChange ? (
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            Preselected when they open it:
          </Typography>
          <RadioGroup
            row
            value={normalizeLadder(preselected)}
            onChange={(e) => onPreselectedChange(e.target.value)}
            aria-label="Preselected ladder"
          >
            {LADDER_OPTIONS.map(({ value: optionValue, label }) => (
              <FormControlLabel
                key={optionValue}
                value={optionValue}
                control={<Radio size="small" />}
                label={<span style={{ fontSize: "12px" }}>{label}</span>}
                sx={{ mr: 2 }}
              />
            ))}
          </RadioGroup>
        </Box>
      ) : null}
    </Box>
  );
};

export default LadderField;
